import {
  type NoveltyCalcErrorGroup,
  NoveltyCalcLaunchStatusFilter,
  type NoveltyCalcRequest,
} from '@/api';
import type { DefaultOptionType } from '@/components/combobox/combobox';
import PriceUnitsCombobox from '@/components/combobox/price-units-combobox';
import LeftSidePanel from '@/components/left-side-panel';
import {
  Button,
  HelpText,
  Label,
  RadioButton,
  Toggle,
} from '@lego/connect-components-react';
import { useEffect, useState } from 'react';
import ConditionsSection from './conditions-section';
import NoveltyCalcErrorPopover from './novelty-calc-error-popover';

type PriceCalcSetupPanelProps = {
  form: NoveltyCalcRequest;
  setForm: React.Dispatch<React.SetStateAction<NoveltyCalcRequest>>;
  selectedPriceUnits: DefaultOptionType[];
  setSelectedPriceUnits: React.Dispatch<
    React.SetStateAction<DefaultOptionType[]>
  >;
  onSubmit: () => void;
  isLoading: boolean;
  priceUnitHelpText: string | null;
  onClearErrors: () => void;
  noveltyCalcErrors: NoveltyCalcErrorGroup[] | null;
};

const PriceCalcSetupPanel = ({
  form,
  setForm,
  selectedPriceUnits,
  setSelectedPriceUnits,
  onSubmit,
  isLoading,
  priceUnitHelpText,
  onClearErrors,
  noveltyCalcErrors,
}: PriceCalcSetupPanelProps) => {
  const currentYear = new Date().getFullYear();
  const nextYear = currentYear + 1;
  const [isErrorPopoverOpen, setIsErrorPopoverOpen] = useState(false);

  const hasCalcErrors =
    noveltyCalcErrors !== null && noveltyCalcErrors.length > 0;

  // biome-ignore lint/correctness/useExhaustiveDependencies: close popover when errors change
  useEffect(() => {
    setIsErrorPopoverOpen(false);
  }, [noveltyCalcErrors]);

  return (
    <LeftSidePanel title="Calculator Settings">
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          width: '100%',
        }}
      >
        <div>
          <Label legend>Year</Label>
          <div style={{ display: 'flex', gap: '16px' }}>
            <RadioButton
              checked={form.planningYear === nextYear}
              label={`Next (${nextYear})`}
              name="year"
              onChange={() => setForm({ ...form, planningYear: nextYear })}
              value="next"
              disabled={isLoading}
            />
            <RadioButton
              checked={form.planningYear === currentYear}
              label={`Current (${currentYear})`}
              name="year"
              onChange={() => setForm({ ...form, planningYear: currentYear })}
              value="current"
              disabled={isLoading}
            />
          </div>
        </div>
        <div style={{ width: '100%' }}>
          <PriceUnitsCombobox
            selectedItems={selectedPriceUnits}
            onSelect={(items) => {
              setSelectedPriceUnits(items as DefaultOptionType[]);
              onClearErrors();
            }}
            title="Local Price List"
            isSingleSelect
            countryOnly
            isDisabled={isLoading}
            hasError={!!priceUnitHelpText || hasCalcErrors}
          />
          {priceUnitHelpText && (
            <HelpText variant="negative">{priceUnitHelpText}</HelpText>
          )}
          {noveltyCalcErrors != null && noveltyCalcErrors.length > 0 && (
            <NoveltyCalcErrorPopover
              errors={noveltyCalcErrors}
              isOpen={isErrorPopoverOpen}
              onToggle={() => setIsErrorPopoverOpen((open) => !open)}
              onClose={() => setIsErrorPopoverOpen(false)}
            />
          )}
        </div>
        {selectedPriceUnits.length > 0 && (
          <ConditionsSection
            priceUnitId={Number(selectedPriceUnits[0]?.id)}
            year={form.planningYear}
          />
        )}
        <div>
          <Label legend>Status</Label>
          <div style={{ display: 'flex', gap: '16px' }}>
            <RadioButton
              checked={
                form.launchStatusFilter === NoveltyCalcLaunchStatusFilter.All
              }
              value={NoveltyCalcLaunchStatusFilter.All}
              label="All Novelties"
              name="status"
              onChange={() =>
                setForm({
                  ...form,
                  launchStatusFilter: NoveltyCalcLaunchStatusFilter.All,
                })
              }
              disabled={isLoading}
            >
              All Novelties
            </RadioButton>
            <RadioButton
              checked={
                form.launchStatusFilter ===
                NoveltyCalcLaunchStatusFilter.FirstHalf
              }
              value={NoveltyCalcLaunchStatusFilter.FirstHalf}
              label="1HY"
              name="status"
              onChange={() =>
                setForm({
                  ...form,
                  launchStatusFilter: NoveltyCalcLaunchStatusFilter.FirstHalf,
                })
              }
              disabled={isLoading}
            >
              1HY
            </RadioButton>
            <RadioButton
              checked={
                form.launchStatusFilter ===
                NoveltyCalcLaunchStatusFilter.SecondHalf
              }
              value={NoveltyCalcLaunchStatusFilter.SecondHalf}
              label="2HY"
              name="status"
              onChange={() =>
                setForm({
                  ...form,
                  launchStatusFilter: NoveltyCalcLaunchStatusFilter.SecondHalf,
                })
              }
              disabled={isLoading}
            >
              2HY
            </RadioButton>
          </div>
        </div>
        <Toggle
          label="Overwrite preliminary prices"
          checked={form.overwritePreliminaryPrices}
          onChange={(event) =>
            setForm({
              ...form,
              overwritePreliminaryPrices: event.target.checked,
            })
          }
          aria-label="Overwrite preliminary prices"
          data-test="overwrite-prices-toggle"
          size="small"
          indicatorTextChecked="On"
          indicatorTextUnchecked="Off"
          disabled={isLoading}
        />
        <Button
          onClick={onSubmit}
          size="medium"
          variant="primary"
          fullWidth
          style={{ boxSizing: 'border-box' }} //remove this when temporary-klik-provider is removed
          disabled={selectedPriceUnits.length === 0}
          loading={isLoading}
        >
          Calculate prices
        </Button>
      </div>
    </LeftSidePanel>
  );
};

export default PriceCalcSetupPanel;
